<script module lang="ts">
	import type { ModernBadgeVariant } from '#lib/modern/ModernBadge.svelte';

	export interface ParsedHttpLog {
		method: string;
		url: string;
	}

	export interface NormalizedApiLog {
		id: string;
		method: string;
		url: string;
		payload?: unknown;
		timestamp: string;
		status?: number;
		durationMs?: number;
		response?: unknown;
		errorResponse?: unknown;
		isError?: boolean;
	}

	export function parseApiLog(log: string): ParsedHttpLog {
		const colonIdx = log.indexOf(':');
		if (colonIdx === -1) {
			return { method: '', url: log };
		}
		return {
			method: log.substring(0, colonIdx).trim(),
			url: log.substring(colonIdx + 1).trim()
		};
	}

	export function normalizeApiLog(log: unknown): NormalizedApiLog {
		if (typeof log === 'string') {
			const parsed = parseApiLog(log);
			return {
				id: `legacy-${parsed.method}-${parsed.url}`,
				method: parsed.method,
				url: parsed.url,
				payload: undefined,
				timestamp: ''
			};
		}
		if (log && typeof log === 'object') {
			const entry = log as {
				id?: string;
				method?: string;
				url?: string;
				payload?: unknown;
				timestamp?: string;
				status?: number;
				durationMs?: number;
				response?: unknown;
				errorResponse?: unknown;
				isError?: boolean;
			};
			const response = entry.response ?? entry.errorResponse;
			const isError =
				entry.isError ??
				(entry.status !== undefined
					? entry.status >= 400 || entry.status === 0
					: undefined);
			return {
				id: entry.id || `api-${entry.method}-${entry.url}`,
				method: entry.method || '',
				url: entry.url || '',
				payload: entry.payload,
				timestamp: entry.timestamp || '',
				...(entry.status !== undefined ? { status: entry.status } : {}),
				...(entry.durationMs !== undefined ? { durationMs: entry.durationMs } : {}),
				...(response !== undefined ? { response } : {}),
				...(entry.errorResponse !== undefined
					? { errorResponse: entry.errorResponse }
					: {}),
				...(isError !== undefined ? { isError } : {})
			};
		}
		return { id: '', method: '', url: String(log || ''), payload: undefined, timestamp: '' };
	}

	export function getHttpMethodVariant(method: string): ModernBadgeVariant {
		switch (method.toUpperCase()) {
			case 'GET':
				return 'info';
			case 'POST':
				return 'success';
			case 'PUT':
				return 'warning';
			case 'PATCH':
				return 'purple';
			case 'DELETE':
				return 'error';
			default:
				return 'neutral';
		}
	}
</script>

<script lang="ts">
	import ModernBadge from '#lib/modern/ModernBadge.svelte';

	interface Props {
		method: string;
		className?: string;
	}

	let { method, className = '' }: Props = $props();

	const variant = $derived(getHttpMethodVariant(method));
</script>

<ModernBadge
	{variant}
	className="font-mono font-bold uppercase text-2xs px-2 py-0.5 min-w-[54px] text-center justify-center tracking-wider shrink-0 {className}"
>
	{method.toUpperCase()}
</ModernBadge>
